import { useEffect, useState } from 'react';
import type { DocsLink } from '../types';
import { getMovementManufacturerById, getWatchBrandById } from '../services/firebase';

export type DocsEntityKind = 'manufacturer' | 'brand';

/**
 * Resolved docs per entity, keyed `"{kind}:{id}"`.
 *
 * Module-level rather than per-component: `DocsLinkChip` is rendered inside
 * modals that mount and unmount constantly (the calibre editor opens on every
 * reference edit), and a per-component cache would re-read the same document
 * every time one reopened. An entry is present-and-empty for an entity with no
 * docs, so a negative answer is cached as firmly as a positive one.
 */
const cache = new Map<string, DocsLink[]>();

/** In-flight reads, so two chips for the same entity share one `getDoc`. */
const inFlight = new Map<string, Promise<DocsLink[]>>();

function cacheKey(kind: DocsEntityKind, id: string): string {
  return `${kind}:${id}`;
}

async function fetchDocsLinks(
  kind: DocsEntityKind,
  id: string,
): Promise<DocsLink[]> {
  const entity =
    kind === 'manufacturer'
      ? await getMovementManufacturerById(id)
      : await getWatchBrandById(id);
  return entity?.docs ?? [];
}

/**
 * Drop cached docs so the next chip re-reads them.
 *
 * Called by the two editors after a save. Without an id, clears everything —
 * cheap, and the honest response to "something changed and I'm not sure what".
 */
export function invalidateDocsLinks(kind?: DocsEntityKind, id?: string): void {
  if (kind && id) {
    cache.delete(cacheKey(kind, id));
    return;
  }
  cache.clear();
}

/** Test seam — resets both maps between cases. */
export function __resetDocsLinksCache(): void {
  cache.clear();
  inFlight.clear();
}

/**
 * Lazily resolve an entity's {@link DocsLink} array by id.
 *
 * The manufacturer and brand listings both render from Algolia, whose
 * projections deliberately carry a narrow subset of each document — `docs` is
 * not among them, and indexing it would mean a watchlock PR for a field only
 * the admin app reads. So the surfaces that only hold an id fetch the document
 * on demand instead: one `getDoc` per entity per session, shared across chips
 * and cached across modal remounts.
 *
 * Callers that ALREADY hold the full document (the two editors, and the calibre
 * modal's brand list, which comes from `getWatchBrands()`) should pass
 * `entity.docs` straight to the chip rather than calling this.
 */
export function useDocsLinks(
  kind: DocsEntityKind,
  /**
   * Accepts `null` as well as `undefined` — several entity types in the admin
   * (`CustomCalibreEntry.manufacturerId` among them) model "not set" as an
   * explicit null, and making every call site coalesce would be noise.
   */
  id: string | null | undefined,
): { docs: DocsLink[]; loading: boolean } {
  const trimmed = id?.trim() ?? '';
  const key = trimmed ? cacheKey(kind, trimmed) : '';
  const [docs, setDocs] = useState<DocsLink[]>(() =>
    key ? cache.get(key) ?? [] : [],
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key) {
      setDocs([]);
      setLoading(false);
      return;
    }
    const cached = cache.get(key);
    if (cached) {
      setDocs(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    // Deduped: a screen showing the brand chip and the manufacturer chip for
    // the same entity issues one read, not two.
    let pending = inFlight.get(key);
    if (!pending) {
      pending = fetchDocsLinks(kind, trimmed)
        .then((result) => {
          cache.set(key, result);
          return result;
        })
        .finally(() => {
          inFlight.delete(key);
        });
      inFlight.set(key, pending);
    }
    pending
      .then((result) => {
        if (!cancelled) setDocs(result);
      })
      .catch((err) => {
        // A missing docs chip is never worth breaking the screen it sits on.
        console.warn(`Could not load docs links for ${key}`, err);
        if (!cancelled) setDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, kind, trimmed]);

  return { docs, loading };
}
